import { useEffect, useRef } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { apiClient } from '@/api/client'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { isConsolePath } from '@/components/console/routes'
import { currentPathname } from '@/utils/currentPathname'
import { ACCESS_CHANGED_MESSAGE, ACCESS_CHANGED_TITLE } from '@/utils/permissionMessages'

/**
 * GlobalErrorHandler sets up the API client to automatically handle:
 * - 422 (Business Rule) errors: displays validation error toasts, except inside the console
 * - 403 (Forbidden) errors: refreshes the user profile, and reports a role change if there was one
 */
export function GlobalErrorHandler() {
  const { showValidationErrors, showWarning } = useToast()
  const { refreshUserProfile } = useAuth()
  const location = useLocation()
  const lastForbiddenRefresh = useRef(0)
  // ⚑ `currentPathname`, not `location.url` — url carries `?query` and `#hash`, so an anchored match
  // misses `/workspace/w1/console?ask=…` (a real URL: the front door's fixed intent) and fires on a
  // legacy route whose query merely mentions /console. Read through a ref so navigating does not
  // re-register the handler.
  const path = useRef(currentPathname(location))
  path.current = currentPathname(location)

  useEffect(() => {
    // Set up the global validation error handler for 422 responses
    apiClient.setValidationErrorHandler((errors) => {
      // ⚑ The console reports its own failures, inline and beside the field that caused them, so a
      // toast here would say the same sentence twice — in a design system the console shares nothing
      // with, in a corner, on a 7-second timer. This is the one place that can tell: the handler is
      // registered once on a shared apiClient and there is no per-call opt-out.
      if (isConsolePath(path.current)) return
      showValidationErrors(errors)
    })
  }, [showValidationErrors])

  useEffect(() => {
    // Set up the global forbidden handler for 403 responses
    apiClient.setForbiddenHandler(() => {
      const now = Date.now()
      // Debounce: skip if we refreshed within the last 5 seconds
      if (now - lastForbiddenRefresh.current < 5000) return
      lastForbiddenRefresh.current = now

      // Only speak when the refresh proves an admin moved this user's access. A 403 is far more often
      // somebody meeting a limit they always had, and that case is announced by the action's own
      // handler with a title worth reading; saying "your permissions have been updated" on top of it
      // was both a second toast and a false alarm (ORB-698).
      void refreshUserProfile().then(rolesChanged => {
        if (rolesChanged) showWarning(ACCESS_CHANGED_TITLE, ACCESS_CHANGED_MESSAGE)
      })
    })
  }, [showWarning, refreshUserProfile])

  // This component doesn't render anything
  return null
}
